<template>
  <div class="appointment-widget" data-test="appointment-widget" :style="brandStyle">
    <div class="widget-header">
      <div class="widget-icon"><i class="fas fa-calendar-check"></i></div>
      <div class="widget-heading">
        <h3 class="widget-title">{{ t('website.home.widget.title') }}</h3>
        <p class="widget-subtitle">{{ t('website.home.widget.subtitle') }}</p>
      </div>
    </div>

    <div v-if="widgetLoading" class="widget-loading">
      <span class="spinner-dot"></span>
    </div>

    <template v-else>
      <div v-if="!successMessage" class="field professional-picker">
        <label>{{ t('website.home.widget.professional') }}</label>
        <div v-if="professionals.length > 1" class="professionals-shell search-wrapper">
          <div class="search-box">
            <i class="fas fa-search search-icon"></i>
            <input
              v-model="searchQuery"
              type="search"
              :placeholder="t('website.home.widget.searchPlaceholder')"
              class="search-input"
              @input="filterProfessionals"
            />
            <button v-if="searchQuery" type="button" class="search-clear" @click="searchQuery = ''">
              <i class="fas fa-times"></i>
            </button>
          </div>

          <div class="professionals-row">
            <button
              type="button"
              class="prof-scroll-btn"
              data-test="prof-scroll-left"
              :disabled="!canScrollProfessionsLeft"
              :aria-label="t('website.home.widget.scrollProfessionalsPrev')"
              @click="scrollProfessions(-1)"
            >
              <i class="fas fa-chevron-left"></i>
            </button>

            <div
              ref="professionsRef"
              class="professionals-list"
              data-test="professions-list"
              @scroll="updateProfessionsScrollState"
            >
              <div
                v-for="p in filteredProfessionals"
                :key="p.id"
                class="professional-pick-card"
                :class="{ selected: p.id === selectedProfessionalId }"
                :style="{
                  animationDelay: `${
                    (filteredProfessionals.findIndex((x) => x.id === p.id) % 6) * 0.06
                  }s`,
                }"
                @click="selectProfessional(p)"
              >
                <div class="pick-avatar-wrapper">
                  <img v-if="p.avatar" :src="p.avatar" :alt="p.name" class="pick-avatar" />
                  <div v-else class="pick-avatar-placeholder">
                    <i class="fas fa-user-circle"></i>
                  </div>
                  <span v-if="p.id === selectedProfessionalId" class="pick-check"
                    ><i class="fas fa-check"></i
                  ></span>
                </div>
                <div class="pick-details">
                  <h3 class="pick-name">{{ p.name }}</h3>
                  <p v-if="p.location" class="pick-location">
                    <i class="fas fa-map-marker-alt"></i> {{ locationLabel(p.location) }}
                  </p>
                </div>
                <div class="pick-action">
                  <span>{{
                    p.id === selectedProfessionalId ? 'Seleccionada' : 'Seleccionar'
                  }}</span>
                  <i
                    class="fas"
                    :class="
                      p.id === selectedProfessionalId ? 'fa-check-circle' : 'fa-chevron-right'
                    "
                  ></i>
                </div>
              </div>
              <div
                v-if="searchQuery && filteredProfessionals.length === 0"
                class="no-results-card"
                data-test="no-results-card"
              >
                <i class="fas fa-search-minus no-results-icon"></i>
                <h3>No se encontraron profesionales</h3>
                <p>Intenta ajustar tu búsqueda.</p>
              </div>
            </div>

            <button
              type="button"
              class="prof-scroll-btn"
              data-test="prof-scroll-right"
              :disabled="!canScrollProfessionsRight"
              :aria-label="t('website.home.widget.scrollProfessionalsNext')"
              @click="scrollProfessions(1)"
            >
              <i class="fas fa-chevron-right"></i>
            </button>
          </div>
        </div>
        <div v-else class="professional-single">
          <span class="professional-avatar"><i class="fas fa-user-circle"></i></span>
          <span>{{ activeProfessional?.name || '' }}</span>
        </div>

        <div v-if="professionals.length > 1" class="step-hint">
          {{ t('website.home.widget.hintCard') }}
        </div>
      </div>

      <div v-if="!successMessage && services.length > 0" class="field">
        <label>{{ t('website.home.widget.specialty') }}</label>
        <div class="pill-row">
          <button
            v-for="s in services"
            :key="s.id"
            type="button"
            class="pill"
            :class="{ active: selectedSpecialtyId === s.id }"
            @click="selectService(s.id)"
          >
            {{ s.name }}
            <span v-if="s.duration_blocks" class="pill-duration"
              >· {{ durationLabel(s.duration_blocks) }}</span
            >
          </button>
        </div>
      </div>

      <div v-if="!successMessage" class="widget-body">
        <div v-if="slotsLoading" class="widget-loading inner">
          <span class="spinner-dot"></span>
        </div>

        <div v-else-if="loadErrorText" class="widget-error">
          <i class="fas fa-exclamation-triangle"></i>
          <p>{{ loadErrorText }}</p>
          <button type="button" class="link-btn" @click="init">
            {{ t('website.home.widget.retry') }}
          </button>
        </div>

        <template v-else-if="totalSlots > 0">
          <div class="slots-heading">
            <p class="section-label">{{ t('website.home.widget.timeHeader') }}</p>
            <span v-if="timezoneLabel" class="timezone-pill">
              <i class="fas fa-globe"></i> {{ timezoneLabel }}
            </span>
          </div>

          <div v-if="localError" class="inline-error">
            <i class="fas fa-exclamation-circle"></i> {{ localError }}
          </div>

          <div class="day-pills-shell">
            <button
              type="button"
              class="day-scroll-btn"
              data-test="day-scroll-left"
              :disabled="!canScrollLeft"
              :aria-label="t('website.home.widget.scrollDaysPrev')"
              @click="scrollDays(-1)"
            >
              <i class="fas fa-chevron-left"></i>
            </button>

            <div
              ref="dayPillsRef"
              class="day-pills"
              data-test="day-pills"
              @scroll="updateDayScrollState"
            >
              <button
                v-for="group in slotGroups"
                :key="group.date"
                type="button"
                class="day-pill"
                :class="{ active: activeDate === group.date }"
                @click="activeDate = group.date"
              >
                <span class="day-label">{{ group.label }}</span>
                <span class="day-count">{{ group.slots.length }}</span>
              </button>
            </div>

            <button
              type="button"
              class="day-scroll-btn"
              data-test="day-scroll-right"
              :disabled="!canScrollRight"
              :aria-label="t('website.home.widget.scrollDaysNext')"
              @click="scrollDays(1)"
            >
              <i class="fas fa-chevron-right"></i>
            </button>
          </div>

          <div class="time-pills">
            <button
              v-for="slot in activeGroupSlots"
              :key="slot.datetime"
              type="button"
              class="time-pill"
              :class="{ selected: isSelected(slot) }"
              @click="selectSlot(slot)"
            >
              {{ slot.time }}
            </button>
          </div>
        </template>

        <div v-else class="widget-empty">
          <i class="fas fa-calendar-times"></i>
          <p>{{ t('website.home.widget.noSlots') }}</p>
          <router-link :to="{ name: 'index' }" class="link-btn solid">
            {{ t('website.home.widget.noSlotsCta') }}
          </router-link>
        </div>
      </div>

      <form v-if="selectedSlot && !successMessage" class="widget-form" @submit.prevent="confirm">
        <div class="form-grid">
          <div class="form-field">
            <label for="widget-name">{{ t('website.home.widget.name') }}</label>
            <input
              id="widget-name"
              v-model="form.name"
              type="text"
              :placeholder="t('website.home.widget.namePlaceholder')"
              :disabled="submitting"
            />
          </div>

          <div class="form-field">
            <label for="widget-email">{{ t('website.home.widget.email') }}</label>
            <input
              id="widget-email"
              v-model="form.email"
              type="email"
              :placeholder="t('website.home.widget.emailPlaceholder')"
              :disabled="submitting"
            />
          </div>

          <div class="form-field">
            <label for="widget-phone">{{ t('website.home.widget.phone') }}</label>
            <div class="phone-input-wrap">
              <span v-if="showCountry" class="dial-code">{{ dialCode }}</span>
              <input
                id="widget-phone"
                v-model="form.phone"
                type="tel"
                :class="{ 'with-dial': showCountry }"
                :placeholder="t('website.home.widget.phonePlaceholder')"
                :disabled="submitting"
              />
            </div>
          </div>

          <div v-if="showCountry" class="form-field">
            <label for="widget-country">{{ t('website.home.widget.country') }}</label>
            <select
              id="widget-country"
              v-model="form.country"
              class="widget-select"
              :disabled="submitting"
            >
              <option v-for="c in countries" :key="c.code" :value="c.code">
                {{ c.flag }} {{ c.code }}
              </option>
            </select>
          </div>
        </div>

        <div v-if="submitError" class="form-error">
          <i class="fas fa-exclamation-triangle"></i> {{ submitError }}
        </div>

        <button type="submit" class="widget-cta" :disabled="submitting">
          <span v-if="submitting" class="spinner-dot light"></span>
          <template v-else>
            <i class="fas fa-check-circle"></i> {{ t('website.home.widget.book') }}
          </template>
        </button>
        <p class="privacy-note">
          <i class="fas fa-lock"></i> {{ t('website.home.widget.secondary') }}
        </p>
      </form>

      <div v-if="successMessage" class="widget-success">
        <div class="success-icon"><i class="fas fa-check"></i></div>
        <h4>{{ t('website.home.widget.successTitle') }}</h4>
        <p>{{ t('website.home.widget.successText') }}</p>
        <div class="appointment-details">
          <p class="detail-label">{{ t('website.home.widget.appointmentFor') }}</p>
          <p class="detail-professional">{{ bookedAppointment?.professional_name ?? '' }}</p>
          <p class="detail-label">{{ t('website.home.widget.appointmentDate') }}</p>
          <p class="detail-datetime">{{ bookedAppointment?.datetime ?? '' }}</p>
        </div>
        <div v-if="bookedAppointment?.professional_phone" class="whatsapp-action">
          <a :href="whatsappUrl" target="_blank" rel="noopener noreferrer" class="whatsapp-btn">
            <i class="fas fa-whatsapp"></i>
            <span>{{ t('website.home.widget.whatsappButton') }}</span>
          </a>
        </div>
        <button type="button" class="link-btn solid" @click="resetAfterBooking">
          {{ t('website.home.widget.bookAnother') }}
        </button>
      </div>
    </template>
  </div>
</template>

<script setup lang="ts">
import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { http as axios } from '@/services/http.service';
import { useAvailabilityStore } from '@/stores/availability.store';
import { useAppointmentsStore } from '@/stores/appointments.store';
import { useSpecialtiesStore, type Specialty } from '@/stores/specialties.store';
import { normalizeTimezone, locationLabel } from '@/composables/useTimezone';
import { loadBranding } from '@/services/branding.service';
import { brandPalette, brandCssVars } from '@/utils/brandPalette';

interface Slot {
  datetime: string;
  time: string;
  date: string;
}

interface Professional {
  id: number;
  name: string;
  avatar?: string;
  location?: string | null;
  include_country?: boolean;
  specialties?: Array<{ id: number; name: string; duration_blocks?: number }>;
  phone?: string | null;
}

interface DayGroup {
  date: string;
  label: string;
  slots: Slot[];
}

const DAYS_AHEAD = 5;
const MAX_SLOTS = 60;

const props = defineProps<{
  professionalId?: number | null;
}>();

const { t, locale } = useI18n();
const availabilityStore = useAvailabilityStore();
const appointmentsStore = useAppointmentsStore();
const specialtiesStore = useSpecialtiesStore();

// Per-tenant palette: the colors configured in the apex admin flow through the
// branding bootstrap. With no colors, `brandPalette` yields black & white.
const branding = loadBranding();
const brandStyle = brandCssVars(brandPalette(branding.color1, branding.color2));

// ---- Day pills horizontal scroll -------------------------------------------------
const dayPillsRef = ref<HTMLElement | null>(null);
const professionsRef = ref<HTMLElement | null>(null);
const canScrollLeft = ref(false);
const canScrollRight = ref(false);
const canScrollProfessionsLeft = ref(false);
const canScrollProfessionsRight = ref(false);

const updateDayScrollState = () => {
  const el = dayPillsRef.value;
  if (!el) return;
  const maxScroll = el.scrollWidth - el.clientWidth;
  canScrollLeft.value = el.scrollLeft > 0;
  canScrollRight.value = el.scrollLeft < maxScroll - 1;
};

// Advances the existing horizontal scroll by roughly one viewport of pills.
const scrollDays = (direction: 1 | -1) => {
  const el = dayPillsRef.value;
  if (!el) return;
  const step = Math.max(el.clientWidth * 0.8, 120);
  el.scrollBy({ left: direction * step, behavior: 'smooth' });
};

const updateProfessionsScrollState = () => {
  const el = professionsRef.value;
  if (!el) return;
  const maxScroll = el.scrollWidth - el.clientWidth;
  canScrollProfessionsLeft.value = el.scrollLeft > 0;
  canScrollProfessionsRight.value = el.scrollLeft < maxScroll - 1;
};

// Scrolls the professionals list by roughly three cards at a time.
const scrollProfessions = (direction: 1 | -1) => {
  const el = professionsRef.value;
  if (!el) return;
  const cardWidth = 240; // card width + gap
  el.scrollBy({ left: direction * cardWidth * 3, behavior: 'smooth' });
};

const onDayPillsResize = () => updateDayScrollState();

const widgetLoading = ref(true);
const slotsLoading = ref(false);
const loadErrorText = ref('');
const professionals = ref<Professional[]>([]);
const searchQuery = ref('');

const filteredProfessionals = computed<Professional[]>(() => {
  const q = searchQuery.value.trim().toLowerCase();
  if (!q) return professionals.value;
  return professionals.value.filter((p) => p.name.toLowerCase().includes(q));
});
const selectedProfessionalId = ref<number | null>(props.professionalId ?? null);
const selectedSpecialtyId = ref<number | null>(null);
const activeDate = ref('');
const selectedSlot = ref<Slot | null>(null);
const localError = ref('');
const submitError = ref('');
const submitting = ref(false);
const successMessage = ref(false);
const bookedAppointment = ref<{
  professional_name?: string | null;
  professional_phone?: string | null;
  datetime?: string | null;
  requester_name?: string | null;
} | null>(null);
let priming = false;

const form = ref({
  name: '',
  email: '',
  phone: '',
  country: 'CL',
});

const countries = [
  { code: 'CL', flag: '🇨🇱', dial: '+56' },
  { code: 'MX', flag: '🇲🇽', dial: '+52' },
  { code: 'AR', flag: '🇦🇷', dial: '+54' },
  { code: 'PE', flag: '🇵🇪', dial: '+51' },
  { code: 'US', flag: '🇺🇸', dial: '+1' },
  { code: 'ES', flag: '🇪🇸', dial: '+34' },
] as const;

const TZ_NAMES: Record<string, string> = {
  'America/Santiago': 'Chile',
  'America/Buenos_Aires': 'Argentina',
  'America/Lima': 'Perú',
  'America/Bogota': 'Colombia',
  'America/Caracas': 'Venezuela',
  'America/La_Paz': 'Bolivia',
  'America/Asuncion': 'Paraguay',
  'America/Montevideo': 'Uruguay',
  'America/Quito': 'Ecuador',
  'America/Mexico_City': 'México',
  'America/New_York': 'USA',
  'America/Chicago': 'USA',
  'America/Denver': 'USA',
  'America/Los_Angeles': 'USA',
  'America/Toronto': 'Canadá',
  'America/Vancouver': 'Canadá',
  'Europe/Madrid': 'España',
  'Europe/Paris': 'Francia',
  'Europe/Berlin': 'Alemania',
  'Europe/London': 'Reino Unido',
  'Europe/Rome': 'Italia',
};

const activeProfessional = computed<Professional | null>(
  () => professionals.value.find((p) => p.id === selectedProfessionalId.value) ?? null,
);

const services = computed<Array<{ id: number; name: string; duration_blocks: number }>>(() => {
  const prof = activeProfessional.value;
  if (!prof) return [];
  const storeMap = new Map<number, Specialty>(specialtiesStore.items.map((s) => [Number(s.id), s]));
  const profSpecs = prof.specialties ?? [];
  if (profSpecs.length === 0) {
    return specialtiesStore.items.map((s) => ({
      id: Number(s.id),
      name: s.name,
      duration_blocks: s.duration_blocks,
    }));
  }
  return profSpecs.map((sp) => ({
    id: Number(sp.id),
    name: storeMap.get(Number(sp.id))?.name ?? sp.name,
    duration_blocks: storeMap.get(Number(sp.id))?.duration_blocks ?? sp.duration_blocks ?? 1,
  }));
});

const selectedSpecialty = computed(
  () => services.value.find((s) => s.id === selectedSpecialtyId.value) ?? null,
);

const timezoneLabel = computed(() => {
  const tz = normalizeTimezone(activeProfessional.value?.location);
  return tz === 'UTC' ? '' : TZ_NAMES[tz] ?? tz;
});

const showCountry = computed(() => activeProfessional.value?.include_country === true);

const whatsappUrl = computed(() => {
  const phone = bookedAppointment.value?.professional_phone;
  if (!phone) return '';
  const digits = phone.toString().replace(/\D/g, '');
  return `https://wa.me/${digits}`;
});

const dialCode = computed(() => countries.find((c) => c.code === form.value.country)?.dial ?? '');

const pad2 = (n: number) => String(n).padStart(2, '0');
const toDateStr = (d: Date) => `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;

const todayStr = toDateStr(new Date());
const tomorrow = new Date();
tomorrow.setDate(tomorrow.getDate() + 1);
const tomorrowStr = toDateStr(tomorrow);

const slotGroups = computed<DayGroup[]>(() => {
  const byDate = new Map<string, Slot[]>();
  for (const slot of availabilityStore.nextSlots) {
    const arr = byDate.get(slot.date) ?? [];
    arr.push(slot);
    byDate.set(slot.date, arr);
  }
  const lc = locale.value === 'es' ? 'es-ES' : 'en-US';
  return [...byDate.entries()].map(([date, slots]) => {
    let label: string;
    if (date === todayStr) label = t('website.home.widget.today');
    else if (date === tomorrowStr) label = t('website.home.widget.tomorrow');
    else {
      label = new Intl.DateTimeFormat(lc, {
        weekday: 'short',
        day: 'numeric',
        month: 'short',
      }).format(new Date(`${date}T00:00:00`));
    }
    return { date, label, slots };
  });
});

const activeGroupSlots = computed<Slot[]>(
  () => slotGroups.value.find((g) => g.date === activeDate.value)?.slots ?? [],
);

const totalSlots = computed(() => availabilityStore.nextSlots.length);

// Recomputed whenever the pills row is (re)rendered or resized: the row is
// hidden while `widgetLoading` is true, so the pending-offset state computed at
// mount would otherwise stay stale and leave both arrows disabled.
let dayPillsObserver: ResizeObserver | null = null;

watch(
  () => [dayPillsRef.value, slotGroups.value.length] as const,
  async ([el]) => {
    await nextTick();
    if (el && typeof ResizeObserver !== 'undefined') {
      dayPillsObserver?.disconnect();
      dayPillsObserver = new ResizeObserver(() => updateDayScrollState());
      dayPillsObserver.observe(el);
    }
    updateDayScrollState();
  },
  { immediate: true, flush: 'post' },
);

// Recomputed whenever the professionals row is (re)rendered or resized.
let professionsObserver: ResizeObserver | null = null;

watch(
  () => [professionsRef.value, professionals.value.length] as const,
  async ([el]) => {
    await nextTick();
    if (el && typeof ResizeObserver !== 'undefined') {
      professionsObserver?.disconnect();
      professionsObserver = new ResizeObserver(() => updateProfessionsScrollState());
      professionsObserver.observe(el);
    }
    updateProfessionsScrollState();
  },
  { immediate: true, flush: 'post' },
);

const isSelected = (slot: Slot) => selectedSlot.value?.datetime === slot.datetime;

const selectSlot = (slot: Slot) => {
  selectedSlot.value = slot;
  localError.value = '';
  submitError.value = '';
};

const selectService = (id: number) => {
  selectedSpecialtyId.value = id;
};

const selectProfessional = (p: Professional) => {
  selectedProfessionalId.value = p.id;
  // Reset service selection when switching professionals
  selectedSpecialtyId.value = null;
  const services = p.specialties ?? [];
  if (services.length === 1) {
    selectedSpecialtyId.value = services[0].id;
  }
};

// Called on each keystroke; searchQuery is already reactive via v-model.
const filterProfessionals = () => {
  // searchQuery updates via v-model; this is a no-op hook for future debounce.
};

const durationLabel = (blocks: number): string => {
  const minutes = blocks * 30;
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  if (h === 0) return `${m} min`;
  return m === 0 ? `${h} h` : `${h} h ${m} min`;
};

// Public professionals list: the interceptor in http.service prefixes the URL
// with the brand's pathPrefix (e.g. /falcon), so the tenant middleware switches
// to the brand's schema and Professional.all reads only this brand's rows.
// Verified: GET /falcon/api/v1/professionals returns only Falcon professionals.
const fetchProfessionals = async () => {
  if (props.professionalId) {
    const { data } = await axios.get(`/api/v1/professionals/${props.professionalId}`);
    professionals.value = [data as Professional];
    selectedProfessionalId.value = props.professionalId;
    return;
  }
  const { data } = await axios.get('/api/v1/professionals');
  professionals.value = Array.isArray(data) ? (data as Professional[]) : [];
  if (professionals.value.length > 0) {
    selectedProfessionalId.value = professionals.value[0].id;
  }
};

const primeSpecialty = () => {
  priming = true;
  try {
    const srvs = services.value;
    selectedSpecialtyId.value = srvs.length > 0 ? srvs[0].id : null;
  } finally {
    priming = false;
  }
};

const loadSlots = async () => {
  const pid = selectedProfessionalId.value;
  if (!pid) return;
  slotsLoading.value = true;
  localError.value = '';
  selectedSlot.value = null;
  try {
    await availabilityStore.fetchNextSlots(
      pid,
      selectedSpecialtyId.value,
      MAX_SLOTS,
      null,
      DAYS_AHEAD,
    );
    loadErrorText.value = '';
    const firstWithSlots = slotGroups.value.find((g) => g.slots.length > 0);
    activeDate.value = firstWithSlots?.date ?? '';
    await nextTick();
    updateDayScrollState();
  } catch {
    loadErrorText.value = t('website.home.widget.loadError');
  } finally {
    slotsLoading.value = false;
  }
};

const init = async () => {
  widgetLoading.value = true;
  loadErrorText.value = '';
  try {
    await specialtiesStore.fetchAll();
    await fetchProfessionals();
    if (selectedProfessionalId.value) {
      primeSpecialty();
      await loadSlots();
    }
  } catch {
    loadErrorText.value = t('website.home.widget.loadError');
  } finally {
    widgetLoading.value = false;
  }
};

watch(
  () => selectedProfessionalId.value,
  () => {
    if (!selectedProfessionalId.value) return;
    activeDate.value = '';
    selectedSlot.value = null;
    submitError.value = '';
    localError.value = '';
    primeSpecialty();
    void loadSlots();
  },
  { immediate: true },
);

watch(
  () => selectedSpecialtyId.value,
  () => {
    if (priming) return;
    if (selectedProfessionalId.value) void loadSlots();
  },
);

const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const focusField = (id: string) => {
  setTimeout(() => document.getElementById(id)?.focus(), 10);
};

const confirm = async () => {
  submitError.value = '';
  if (!selectedSlot.value) return;
  if (services.value.length === 0 || !selectedSpecialtyId.value) {
    submitError.value = t('website.home.widget.requiredService');
    return;
  }
  if (!form.value.name.trim()) {
    submitError.value = t('website.home.widget.requiredField', {
      field: t('website.home.widget.fieldName'),
    });
    focusField('widget-name');
    return;
  }
  if (!form.value.email.trim()) {
    submitError.value = t('website.home.widget.requiredField', {
      field: t('website.home.widget.fieldEmail'),
    });
    focusField('widget-email');
    return;
  }
  if (!emailPattern.test(form.value.email.trim())) {
    submitError.value = t('website.home.widget.invalidEmail');
    focusField('widget-email');
    return;
  }
  if (!form.value.phone.trim()) {
    submitError.value = t('website.home.widget.requiredField', {
      field: t('website.home.widget.fieldPhone'),
    });
    focusField('widget-phone');
    return;
  }
  if (showCountry.value && !form.value.country) {
    submitError.value = t('website.home.widget.requiredField', {
      field: t('website.home.widget.fieldCountry'),
    });
    focusField('widget-country');
    return;
  }

  const prof = activeProfessional.value;
  if (!prof || !selectedSlot.value) return;

  submitting.value = true;
  try {
    const created = await appointmentsStore.createOne({
      professional_id: prof.id,
      date_time: selectedSlot.value.datetime,
      requester_email: form.value.email.trim(),
      requester_name: form.value.name.trim(),
      requester_phone: showCountry.value
        ? `${dialCode.value} ${form.value.phone.trim()}`
        : form.value.phone.trim(),
      ...(showCountry.value ? { requester_country: form.value.country } : {}),
      specialty_id: selectedSpecialtyId.value ?? undefined,
    });
    availabilityStore.applySlotOccupied(prof.id, {
      datetime: selectedSlot.value.datetime,
      duration_blocks: selectedSpecialty.value?.duration_blocks ?? 1,
    });
    bookedAppointment.value = {
      professional_name: prof.name,
      professional_phone: prof.phone ?? null,
      datetime: created.datetime ?? selectedSlot.value.datetime,
      requester_name: form.value.name.trim(),
    };
    successMessage.value = true;
  } catch (err) {
    const message = extractError(err);
    if (isSlotTakenMessage(message)) {
      localError.value = message;
      availabilityStore.applySlotOccupied(prof.id, {
        datetime: selectedSlot.value.datetime,
        duration_blocks: selectedSpecialty.value?.duration_blocks ?? 1,
      });
      selectedSlot.value = null;
    } else {
      submitError.value = message;
    }
  } finally {
    submitting.value = false;
  }
};

const extractError = (err: unknown): string => {
  if (typeof err === 'string' && err !== 'Unknown Error') return err;
  if (err && typeof err === 'object') {
    const d = err as { error?: unknown; errors?: unknown };
    if (typeof d.error === 'string' && d.error) return d.error;
    if (Array.isArray(d.errors) && d.errors.length > 0) return String(d.errors[0]);
  }
  return t('website.home.widget.genericError');
};

const isSlotTakenMessage = (msg: string) =>
  /reservado|reserved|already been taken|no disponible|not available|no longer/i.test(msg);

const resetAfterBooking = () => {
  successMessage.value = false;
  bookedAppointment.value = null;
  form.value.name = '';
  form.value.email = '';
  form.value.phone = '';
  selectedSlot.value = null;
};

onMounted(() => {
  void init();
  window.addEventListener('resize', onDayPillsResize);
  void nextTick(updateDayScrollState);
});

onBeforeUnmount(() => {
  window.removeEventListener('resize', onDayPillsResize);
  dayPillsObserver?.disconnect();
});
</script>

<style scoped>
.appointment-widget {
  background: white;
  border-radius: 24px;
  padding: clamp(1.25rem, 4vw, 2.25rem);
  box-shadow: 0 18px 50px rgba(var(--brand-primary-rgb), 0.16);
  border: 1px solid rgba(var(--brand-primary-rgb), 0.1);
  position: relative;
  overflow: hidden;
  font-family:
    'Inter',
    -apple-system,
    BlinkMacSystemFont,
    'Segoe UI',
    Roboto,
    sans-serif;
  text-align: left;
}

.appointment-widget::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 5px;
  background: var(--brand-gradient);
}

.widget-header {
  display: flex;
  align-items: flex-start;
  gap: 14px;
  margin-bottom: 22px;
}

.widget-icon {
  width: 46px;
  height: 46px;
  flex-shrink: 0;
  background: var(--brand-primary-soft);
  border-radius: 13px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.3rem;
  color: var(--brand-primary);
}

.widget-title {
  margin: 0 0 3px;
  font-size: 1.15rem;
  font-weight: 900;
  color: #1a1a1a;
}

.widget-subtitle {
  margin: 0;
  font-size: 0.85rem;
  color: #6b7280;
  line-height: 1.4;
}

.field {
  display: flex;
  flex-direction: column;
  gap: 7px;
  margin-bottom: 16px;
}

.field label,
.form-field label {
  font-size: 0.8rem;
  font-weight: 700;
  color: #475569;
  text-transform: uppercase;
  letter-spacing: 0.03em;
}

.widget-select,
.form-field input {
  width: 100%;
  padding: 13px 16px;
  border: 2px solid #e9e2f4;
  border-radius: 14px;
  font-size: 0.95rem;
  color: #1e293b;
  background: white;
  transition: border-color 0.2s;
  box-sizing: border-box;
}

.widget-select:focus,
.form-field input:focus {
  outline: none;
  border-color: var(--brand-primary);
  box-shadow: 0 0 0 4px rgba(var(--brand-primary-rgb), 0.08);
}

.professional-single {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 11px 16px;
  background: var(--brand-primary-soft);
  border-radius: 14px;
  font-weight: 700;
  color: var(--brand-ink);
}

.professional-avatar {
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background: var(--brand-gradient);
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.9rem;
}

/* --- Professional pick cards (horizontal scrollable list) --- */
.professional-picker {
  margin-bottom: 16px;
}

/* Search bar always appears ABOVE the professionals list — not beside it.
   The shell is a vertical column: search-box on top, professionals-row
   (scroll buttons + list) below. */
.professionals-shell {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.search-box {
  position: relative;
  width: 100%;
  max-width: 100%;
  flex: none;
}

.search-box .search-icon {
  position: absolute;
  left: 10px;
  top: 50%;
  transform: translateY(-50%);
  color: #94a3b8;
  font-size: 0.85rem;
}

.search-input {
  width: 100%;
  padding: 8px 34px 8px 34px;
  border: 1.5px solid #e2e8f0;
  border-radius: 99px;
  font-size: 0.85rem;
  background: #fdfbff;
  transition: all 0.2s;
  box-sizing: border-box;
}

.search-input:focus {
  outline: none;
  border-color: var(--brand-primary);
  box-shadow: 0 0 0 3px rgba(var(--brand-primary-rgb), 0.08);
  background: white;
}

.search-clear {
  position: absolute;
  right: 10px;
  top: 50%;
  transform: translateY(-50%);
  background: transparent;
  border: none;
  color: #94a3b8;
  font-size: 0.75rem;
  cursor: pointer;
  padding: 0;
}

.search-clear:hover {
  color: #64748b;
}

.step-hint {
  margin-top: 10px;
  font-size: 0.8rem;
  color: #6b7280;
  text-align: center;
}

.no-results-card {
  text-align: center;
  padding: 24px 16px;
  background: #f8fafc;
  border-radius: 16px;
  margin-top: 8px;
}

.no-results-icon {
  font-size: 1.8rem;
  color: #cbd5e1;
  margin-bottom: 8px;
}

.no-results-card h3 {
  margin: 0 0 4px;
  font-size: 0.95rem;
  color: #475569;
}

.no-results-card p {
  margin: 0;
  font-size: 0.82rem;
  color: #94a3b8;
}

.prof-scroll-btn {
  flex-shrink: 0;
  width: 30px;
  height: 30px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  border: 1.5px solid rgba(var(--brand-primary-rgb), 0.2);
  background: white;
  color: var(--brand-primary);
  font-size: 0.72rem;
  cursor: pointer;
  transition: all 0.2s;
}

.prof-scroll-btn:hover:not(:disabled) {
  background: var(--brand-primary-soft);
  border-color: var(--brand-primary);
}

.prof-scroll-btn:disabled {
  opacity: 0.35;
  cursor: default;
}

.professionals-row {
  display: flex;
  align-items: center;
  gap: 6px;
}

.professionals-list {
  flex: 1;
  display: flex;
  gap: 12px;
  overflow-x: auto;
  padding-bottom: 4px;
  scrollbar-width: none;
}

.professions-list::-webkit-scrollbar {
  display: none;
}

.professional-pick-card {
  flex: 0 0 auto;
  width: 220px;
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
  background: linear-gradient(180deg, #ffffff 0%, #fdfaff 100%);
  border: 2px solid #e9e2f4;
  border-radius: 16px;
  padding: 16px 12px 14px;
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.165, 0.84, 0.44, 1);
  animation: rise 0.5s cubic-bezier(0.165, 0.84, 0.44, 1) both;
  box-shadow: 0 6px 18px rgba(0, 0, 0, 0.04);
}

.professional-pick-card:hover {
  transform: translateY(-3px);
  box-shadow: 0 12px 26px rgba(var(--brand-primary-rgb), 0.12);
  border-color: rgba(var(--brand-primary-rgb), 0.4);
}

.professional-pick-card.selected {
  border-color: var(--brand-primary);
  background: linear-gradient(180deg, rgba(var(--brand-primary-rgb), 0.08) 0%, #ffffff 100%);
  box-shadow: 0 10px 24px rgba(var(--brand-primary-rgb), 0.18);
}

.pick-avatar-wrapper {
  position: relative;
  width: 60px;
  height: 60px;
}

.pick-avatar,
.pick-avatar-placeholder {
  width: 60px;
  height: 60px;
  border-radius: 50%;
  object-fit: cover;
  border: 3px solid white;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  transition: all 0.3s cubic-bezier(0.165, 0.84, 0.44, 1);
}

.pick-avatar-placeholder {
  background: linear-gradient(
    135deg,
    var(--brand-primary-soft, #f0e6ff) 0%,
    var(--brand-primary, #8d17c4) 100%
  );
  color: white;
  font-size: 1.5rem;
  display: flex;
  align-items: center;
  justify-content: center;
}

.pick-check {
  position: absolute;
  bottom: 2px;
  right: 2px;
  width: 24px;
  height: 24px;
  border-radius: 50%;
  background: var(--brand-primary);
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.65rem;
  box-shadow: 0 2px 8px rgba(var(--brand-primary-rgb), 0.4);
  z-index: 2;
}

.pick-details {
  text-align: center;
  display: flex;
  flex-direction: column;
  gap: 3px;
}

.pick-name {
  font-size: 0.95rem;
  font-weight: 800;
  color: #1a1a1a;
  margin: 0;
  line-height: 1.25;
}

.pick-location {
  margin: 1px 0 0;
  font-size: 0.75rem;
  color: #6b7280;
  font-weight: 500;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 3px;
}

.pick-location i {
  color: var(--brand-primary);
}

.pick-action {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  margin-top: 3px;
  padding: 6px 14px;
  border-radius: 99px;
  border: 2px solid var(--brand-primary);
  color: var(--brand-primary);
  font-weight: 700;
  font-size: 0.75rem;
  background: white;
  transition: all 0.2s ease;
}

.professional-pick-card.selected .pick-action {
  background: var(--brand-primary);
  color: white;
  border-color: transparent;
  box-shadow: 0 3px 10px rgba(var(--brand-primary-rgb), 0.25);
}

.pill-row {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.pill {
  padding: 9px 16px;
  border-radius: 99px;
  border: 1.5px solid #e9e2f4;
  background: #fdfbff;
  font-size: 0.85rem;
  font-weight: 700;
  color: var(--brand-primary);
  cursor: pointer;
  transition: all 0.2s;
}

.pill:hover {
  border-color: var(--brand-primary);
}

.pill-duration {
  font-weight: 600;
  opacity: 0.75;
  font-size: 0.78rem;
}

.pill.active .pill-duration {
  opacity: 0.9;
}

.pill.active {
  background: var(--brand-gradient);
  border-color: transparent;
  color: var(--brand-on-primary);
}

.widget-body {
  margin-top: 20px;
}

.slots-heading {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  margin-bottom: 12px;
}

.section-label {
  margin: 0;
  font-size: 0.9rem;
  font-weight: 800;
  color: var(--brand-ink);
}

.timezone-pill {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  font-size: 0.72rem;
  font-weight: 600;
  color: var(--brand-primary);
  background: var(--brand-primary-soft);
  padding: 4px 10px;
  border-radius: 99px;
  white-space: nowrap;
}

.inline-error {
  display: flex;
  align-items: center;
  gap: 8px;
  background: #fef2f2;
  color: #dc2626;
  border: 1px solid #fee2e2;
  padding: 11px 14px;
  border-radius: 12px;
  font-size: 0.85rem;
  font-weight: 600;
  margin-bottom: 14px;
}

.day-pills-shell {
  display: flex;
  align-items: center;
  gap: 6px;
  margin-bottom: 12px;
}

.day-scroll-btn {
  flex-shrink: 0;
  width: 30px;
  height: 30px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  border: 1.5px solid rgba(var(--brand-primary-rgb), 0.2);
  background: white;
  color: var(--brand-primary);
  font-size: 0.72rem;
  cursor: pointer;
  transition: all 0.2s;
}

.day-scroll-btn:hover:not(:disabled) {
  background: var(--brand-primary-soft);
  border-color: var(--brand-primary);
}

.day-scroll-btn:disabled {
  opacity: 0.35;
  cursor: default;
}

.day-pills {
  flex: 1;
  display: flex;
  gap: 8px;
  overflow-x: auto;
  padding-bottom: 4px;
  scrollbar-width: none;
}

.day-pills::-webkit-scrollbar {
  display: none;
}

.day-pill {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 3px;
  padding: 9px 13px;
  border-radius: 13px;
  border: 1.5px solid rgba(var(--brand-primary-rgb), 0.12);
  background: #fdfbff;
  cursor: pointer;
  transition: all 0.25s;
  min-width: 62px;
  flex-shrink: 0;
}

.day-pill:hover {
  border-color: var(--brand-primary);
}

.day-pill.active {
  background: var(--brand-gradient);
  border-color: transparent;
  color: var(--brand-on-primary);
  box-shadow: 0 8px 20px rgba(var(--brand-primary-rgb), 0.25);
}

.day-label {
  font-size: 0.8rem;
  font-weight: 700;
  color: var(--brand-primary);
  text-transform: capitalize;
}

.day-pill.active .day-label {
  color: var(--brand-on-primary);
}

.day-count {
  font-size: 0.72rem;
  font-weight: 800;
  background: rgba(var(--brand-primary-rgb), 0.12);
  color: var(--brand-primary);
  border-radius: 99px;
  padding: 1px 7px;
}

.day-pill.active .day-count {
  background: rgba(255, 255, 255, 0.25);
  color: var(--brand-on-primary);
}

.time-pills {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(84px, 1fr));
  gap: 9px;
}

.time-pill {
  padding: 13px 10px;
  border-radius: 13px;
  border: 1.5px solid rgba(var(--brand-primary-rgb), 0.15);
  background: #fdfbff;
  cursor: pointer;
  font-size: 0.95rem;
  font-weight: 800;
  color: var(--brand-primary);
  font-variant-numeric: tabular-nums;
  transition: all 0.25s;
}

.time-pill:hover {
  border-color: var(--brand-primary);
  transform: translateY(-2px);
  box-shadow: 0 6px 18px rgba(var(--brand-primary-rgb), 0.12);
}

.time-pill.selected {
  background: var(--brand-gradient);
  border-color: transparent;
  color: var(--brand-on-primary);
  box-shadow: 0 10px 25px rgba(var(--brand-primary-rgb), 0.28);
}

.widget-form {
  margin-top: 22px;
  display: flex;
  flex-direction: column;
  gap: 14px;
}

.form-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
}

.form-field {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.phone-input-wrap {
  position: relative;
}

.dial-code {
  position: absolute;
  left: 16px;
  top: 50%;
  transform: translateY(-50%);
  font-weight: 700;
  color: #64748b;
  font-size: 0.9rem;
  pointer-events: none;
}

.with-dial {
  padding-left: 56px !important;
}

.form-error {
  display: flex;
  align-items: center;
  gap: 8px;
  background: #fef2f2;
  color: #dc2626;
  border: 1px solid #fee2e2;
  padding: 11px 14px;
  border-radius: 12px;
  font-size: 0.85rem;
  font-weight: 600;
}

.widget-cta {
  width: 100%;
  background: var(--brand-gradient);
  color: var(--brand-on-primary);
  border: none;
  padding: 16px;
  border-radius: 15px;
  font-size: 1.05rem;
  font-weight: 800;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  box-shadow: 0 10px 30px rgba(var(--brand-primary-rgb), 0.3);
  transition: all 0.3s;
}

.widget-cta:hover:not(:disabled) {
  transform: translateY(-3px);
  box-shadow: 0 15px 40px rgba(var(--brand-primary-rgb), 0.4);
}

.widget-cta:disabled {
  background: #e2e8f0;
  color: #94a3b8;
  cursor: not-allowed;
  box-shadow: none;
}

.privacy-note {
  margin: 0;
  text-align: center;
  font-size: 0.75rem;
  color: #94a3b8;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 5px;
}

.widget-success {
  margin-top: 20px;
  background: #f0fdf4;
  border: 1px solid #bbf7d0;
  border-radius: 16px;
  padding: 24px 20px;
  text-align: center;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
}

.success-icon {
  width: 52px;
  height: 52px;
  border-radius: 50%;
  background: linear-gradient(135deg, #10b981, #059669);
  color: white;
  font-size: 1.4rem;
  display: flex;
  align-items: center;
  justify-content: center;
}

.widget-success h4 {
  margin: 0;
  font-size: 1.1rem;
  font-weight: 900;
  color: #065f46;
}

.widget-success p {
  margin: 0 0 6px;
  font-size: 0.9rem;
  color: #047857;
  line-height: 1.5;
}

.appointment-details {
  margin: 12px 0 14px;
  text-align: left;
  width: 100%;
}

.detail-label {
  margin: 0 0 2px;
  font-size: 0.75rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: #6b7280;
}

.detail-professional {
  margin: 0 0 8px;
  font-size: 1rem;
  font-weight: 700;
  color: #065f46;
}

.detail-datetime {
  margin: 0 0 6px;
  font-size: 0.95rem;
  color: #047857;
  font-weight: 600;
}

.whatsapp-action {
  margin-top: 14px;
  width: 100%;
}

.whatsapp-btn {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  width: 100%;
  max-width: 320px;
  padding: 12px 16px;
  background: #25d366;
  color: white;
  border: none;
  border-radius: 10px;
  font-size: 0.95rem;
  font-weight: 700;
  text-decoration: none;
  justify-content: center;
  cursor: pointer;
  transition: background 0.2s;
}

.whatsapp-btn:hover {
  background: #1eb053;
}

.whatsapp-btn i {
  font-size: 1.2rem;
}

.widget-empty {
  text-align: center;
  padding: 30px 16px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
  color: #8a7aa0;
}

.widget-empty i {
  font-size: 2rem;
  opacity: 0.5;
}

.widget-empty p {
  margin: 0;
  font-size: 0.9rem;
}

.widget-loading {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 30px 0;
}

.widget-loading.inner {
  padding: 20px 0;
}

.spinner-dot {
  width: 30px;
  height: 30px;
  border: 3px solid rgba(var(--brand-primary-rgb), 0.2);
  border-top-color: var(--brand-primary);
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
  display: inline-block;
}

.spinner-dot.light {
  width: 22px;
  height: 22px;
  border-color: rgba(255, 255, 255, 0.3);
  border-top-color: white;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

@keyframes rise {
  from {
    opacity: 0;
    transform: translateY(18px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.widget-error {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  padding: 24px 16px;
  color: #b45309;
  background: #fffbeb;
  border: 1px solid #fde68a;
  border-radius: 14px;
}

.widget-error i {
  font-size: 1.6rem;
}

.widget-error p {
  margin: 0;
  font-size: 0.9rem;
}

.link-btn {
  border: none;
  background: transparent;
  color: var(--brand-primary);
  font-weight: 700;
  font-size: 0.85rem;
  cursor: pointer;
  text-decoration: none;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 8px 14px;
  border-radius: 99px;
}

.link-btn:hover {
  text-decoration: underline;
}

.link-btn.solid {
  background: var(--brand-primary);
  color: var(--brand-on-primary);
  border-radius: 99px;
  padding: 10px 20px;
}

.link-btn.solid:hover {
  background: var(--brand-primary-dark);
  text-decoration: none;
}

@media (max-width: 640px) {
  .form-grid {
    grid-template-columns: 1fr;
  }

  .appointment-widget {
    border-radius: 20px;
  }
}
</style>
